/**
 * 服务端业务码 → 前端处置。
 *
 * HTTP 状态码不够用：401 底下有好几个含义不同的业务码，403 底下也有。只看 HTTP 状态码
 * 会把「帐号被停用」当成「登录过期」，把人赶回登录页重登一次再被拒一次。
 *
 * 微服务公共码以 `muou-common-api/CommonResultCode.java` 为准，IAM 码以正式接口契约为准。
 * 原单体独有的兼容码仅保留给网关透传；旧业务请求已在统一出口停用。
 */
export const BIZ_CODE = {
  /**
   * 成功码。2026-10-20 起统一为 `200`，与 HTTP 成功对齐。
   *
   * 走网关的接口（`/api/v1/*`，见 `utils/http.ts` 的 `gatewayRequest`）按它判成功。
   */
  IAM_SUCCESS: 200,
  /**
   * iam 后台登录（API-10-131）：邮箱或密码不正确。
   *
   * 留在登录页并走服务端文案：登录页自己把它显示在表单上（`skipErrorHandler`），
   * 不回退、也不重试。原来还拿它当「回退旧单体登录」的判据——IAM-only 之后没有旧路了。
   */
  IAM_LOGIN_CREDENTIAL_INVALID: 10401422,
  /** iam 后台登录（API-10-131）：帐号已停用。密码对了才会回，说明这个人确实是 iam 的帐号 */
  IAM_LOGIN_ACCOUNT_DISABLED: 10401423,
  /** iam 后台登录：帐号在锁定期内 */
  IAM_LOGIN_ACCOUNT_LOCKED: 10401424,
  /** 正式 IAM 后台退出和当前会话的未认证业务码。 */
  IAM_LOGOUT_UNAUTHORIZED: 10401425,
  IAM_SESSION_UNAUTHORIZED: 10401426,
  /** 密码正确但租户已冻结：属于登录拒绝，展示服务端原因。 */
  IAM_LOGIN_TENANT_FROZEN: 10403431,

  UNAUTHORIZED: 401,
  /** 微服务公共契约：会话不存在或已失效。 */
  SESSION_UNAUTHORIZED: 99401000,
  FORBIDDEN: 403,
  TOO_MANY_REQUESTS: 429,

  LOGIN_FAILED: 40101,
  ACCOUNT_DISABLED: 40102,
  ACCOUNT_LOCKED: 40103,
  TOKEN_EXPIRED: 40105,
  ACCOUNT_CLOSED: 40106,
  INITIAL_PASSWORD_EXPIRED: 40107,

  TENANT_DISABLED: 40301,
  MODULE_LOCKED: 40302,
  QUOTA_EXCEEDED: 40303,
  NO_PERMISSION_FOR_PAGE: 40304,
  NOT_IN_YOUR_REPORTING_LINE: 40305,
  /**
   * 缺权限码（公共库的 `PERMISSION_MISSING`，10-09 §5.4）：HTTP 仍是 403，
   * 响应体 `content.missingCodes` 写明缺哪几个码；前端只展示这些码与固定文案。
   */
  PERMISSION_MISSING: 99403006,
  /**
   * 模块未开通（platform 门控）：本会当前档位够不上这个模块。
   * 前端收到后补刷一次门控，同一句文案 2 秒内只弹一条——一页会同时发好几个接口。
   */
  MODULE_GATE_DENIED: 18403001,
} as const;

export interface BizAction {
  /**
   * 服务端未给 msg 时使用的本地词条（`http.*`，三种语言均齐全）。
   * 服务端已翻译文案始终优先，避免覆盖具体失败原因。
   */
  messageKey?: string;
  /** 跳到这个地址；不给则不跳 */
  redirect?: string;
  /** true = 什么都不做，交给调用页面自己处理（例如登录页要把错误显示在表单上） */
  stay?: boolean;
  /**
   * 提示文案里补上响应体 `content.missingCodes`（403 缺权限码专用）。
   * 码为空时退到 `http.permissionMissingNoCodes`，只显示固定文案。
   */
  withMissingCodes?: boolean;
  /**
   * 跳 /login 时附带的原因码（白名单，只在这几个值里选），登录页据此显示常驻说明。
   * 🔴 这里只放代码，不放文案本身——文案在登录页本地映射。整页跳转（`window.location.href`）
   * 会把跳转前弹的提示一并销毁（PRD home-1.md R7：超时退出要在登录页写明原因，
   * 不写的话回来的人会以为系统出错了），所以不能靠 toast，只能靠 URL 带着原因过去。
   */
  loginReason?: 'expired' | 'tenantDisabled';
}

const ACTIONS: Record<number, BizAction> = {
  [BIZ_CODE.IAM_SESSION_UNAUTHORIZED]: {
    redirect: '/login', messageKey: 'http.expired', loginReason: 'expired',
  },
  [BIZ_CODE.IAM_LOGOUT_UNAUTHORIZED]: {
    redirect: '/login', messageKey: 'http.expired', loginReason: 'expired',
  },
  [BIZ_CODE.SESSION_UNAUTHORIZED]: {
    redirect: '/login', messageKey: 'http.expired', loginReason: 'expired',
  },
  // ── 会话真的没了：跳登录 ──
  [BIZ_CODE.UNAUTHORIZED]: {
    redirect: '/login', messageKey: 'http.expired', loginReason: 'expired',
  },
  [BIZ_CODE.TOKEN_EXPIRED]: {
    redirect: '/login', messageKey: 'http.expired', loginReason: 'expired',
  },

  // ── 登录本身失败：留在登录页，别跳 ──
  [BIZ_CODE.IAM_LOGIN_CREDENTIAL_INVALID]: { stay: true },
  [BIZ_CODE.IAM_LOGIN_ACCOUNT_DISABLED]: { stay: true },
  [BIZ_CODE.IAM_LOGIN_ACCOUNT_LOCKED]: { stay: true },
  [BIZ_CODE.IAM_LOGIN_TENANT_FROZEN]: { stay: true },
  [BIZ_CODE.LOGIN_FAILED]: { stay: true },
  [BIZ_CODE.ACCOUNT_LOCKED]: { stay: true, messageKey: 'http.accountLocked' },
  [BIZ_CODE.ACCOUNT_DISABLED]: { stay: true, messageKey: 'http.accountDisabled' },
  [BIZ_CODE.ACCOUNT_CLOSED]: { stay: true, messageKey: 'http.accountClosed' },
  [BIZ_CODE.INITIAL_PASSWORD_EXPIRED]: {
    stay: true, messageKey: 'http.initialPasswordExpired',
  },

  // ── 403 一族：说清楚是哪种「不行」──
  [BIZ_CODE.MODULE_LOCKED]: { messageKey: 'http.moduleLocked' },
  [BIZ_CODE.QUOTA_EXCEEDED]: { messageKey: 'http.quotaExceeded' },
  [BIZ_CODE.TENANT_DISABLED]: {
    redirect: '/login', messageKey: 'http.tenantDisabled', loginReason: 'tenantDisabled',
  },
  [BIZ_CODE.NO_PERMISSION_FOR_PAGE]: { messageKey: 'http.permissionMissingNoCodes' },
  // 缺权限码：只报码 + 固定文案；不重取会话、不提示重新登录（重新登录不改变码集）
  [BIZ_CODE.PERMISSION_MISSING]: {
    messageKey: 'http.permissionMissing',
    withMissingCodes: true,
  },
  // 管理层级：前端算不出上下级关系，只能把服务端的拒绝翻成人话
  [BIZ_CODE.NOT_IN_YOUR_REPORTING_LINE]: { messageKey: 'http.notInReportingLine' },
  // 模块未开通：服务端没给文案时才用本地这句；正常走服务端「需升级至…」
  [BIZ_CODE.MODULE_GATE_DENIED]: { messageKey: 'http.moduleLocked' },

  [BIZ_CODE.TOO_MANY_REQUESTS]: { messageKey: 'http.tooManyRequests' },
};

/**
 * 按业务码取处置；业务码没登记时退回按 HTTP 状态码判断。
 * 未知的 401 一律按「没登录」处理——这是安全的默认。
 */
export function resolveBizAction(code: number | undefined, httpStatus?: number): BizAction {
  if (code !== undefined && ACTIONS[code]) return ACTIONS[code];
  if (httpStatus === 401) return ACTIONS[BIZ_CODE.UNAUTHORIZED];
  return {};
}
